import clsx from 'clsx'

import { useMemo } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'

import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { useBadges } from '@/components/hooks/useBadges'
import { Badge } from 'libs/searchspring/components/Badge'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { usePromolines } from '@/components/hooks/usePromolines'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'
import { ResultsSwatches } from 'libs/searchspring/components/ResultSwatches'
import { useGuides } from '@/components/hooks/useGuides'
import { ResultQuickAdd } from 'libs/searchspring/components/ResultQuickAdd'
import { normalisedArray } from 'libs/searchspring/helpers/normalisers'

const DEFAULT_OPTIONS = {
  hideQuickAdd: false,
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false,
  hideWishlist: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0', eager = false, imageSizes, imageWidths }) {
  const [variant] = result.variants
  const {
    hideQuickAdd = DEFAULT_OPTIONS.hideQuickAdd,
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges
  } = options
  const { currentResult, updateSwatch } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  const criteria = useMemo(() => ({
    tags: normalisedArray(currentResult?.tags) ?? [],
    collections: normalisedArray(currentResult?.collection_handle) ?? [],
    products: [currentResult.handle]
  }), [currentResult?.tags, currentResult?.collection_handle, currentResult?.handle])

  const allBadges = useBadges(criteria)
  const allPromolines = usePromolines(criteria)
  const [sizeGuide] = useGuides(criteria)
  const resultName = currentResult.mfield_utopia_customer_facing_product_name || currentResult.name

  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstBadge, secondBadge] = useMemo(() => getObjects(allBadges), [allBadges])
  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return `/products/${currentResult.handle}`
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    // Collection is a global pulled from template-search.liquid and collection-main.liquid. Ensure value is set in both places :)
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  // This is wrapped in an extra div to support injection from Wishlist KING
  // DON'T REMOVE THAT DIV 🔫
  return (
    <div className={clsx('group/card relative flex', className)} id={`result__${currentResult.handle}`} data-dl-track="select_item" data-dl-product-id={currentResult.uid} data-dl-index={dataIndex}>
      <div className="flex flex-col gap-y-0.625 w-full grow">
        <div className="relative w-full overflow-hidden aspect-4/5">
          <a href={url} onClick={handleLogClick} aria-label={resultName}>
            <ResultImage result={currentResult} eager={eager} sizes={imageSizes} widths={imageWidths}>
              {!hideBadges && (firstBadge || secondBadge) && (
                <div className="absolute top-0 left-0 flex flex-col gap-y-0.125 max-w-[80%]">
                  {firstBadge && <Badge badge={firstBadge} />}
                  {secondBadge && <Badge badge={secondBadge} />}
                </div>
              )}
            </ResultImage>
          </a>
          {/* Quick Add */}
          {(!hideQuickAdd) && (
            <div className="absolute hidden lg:block bottom-0.625 lg:bottom-1 right-0.5 lg:right-1 w-fit lg:w-[calc(100%-2rem)]">
              {!hideQuickAdd && currentResult.is_giftcard !== 'true' && <ResultQuickAdd result={currentResult} firstPromoline={firstPromoline} secondPromoline={secondPromoline} sizeGuide={sizeGuide} />}
            </div>
          )}
        </div>
        <div className='grow flex flex-col gap-y-0.5 px-0.5 lg:px-1'>
          <a href={url} className="paragraph-s lg:paragraph-m link-fade !font-serif" aria-label={resultName} dangerouslySetInnerHTML={{ __html: resultName }}></a>
          {!hideSwatches && <ResultsSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />}
          <Price price={variant.price} compareAtPrice={variant.compare_at_price} className="flex" />
          {!hidePromolines && (firstPromoline || secondPromoline) && (
            <div className="flex flex-col gap-0.25">
              {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="PLP" promolineSize="medium" />}
              {secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="PLP" promolineSize="medium" />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
